import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { UsersIcon } from "lucide-react";

import { InvitationOfType, UseInvitationOptions } from "simplydo/interfaces";

import {
  acceptAuthenticatedInvitation,
  rejectAuthenticatedInvitation,
  removeAuthenticatedInvitation,
  toastError,
  toastMessage,
  toastSuccess,
  withInvitationPrompt,
} from "./internal";
import type { InvitationViewModel } from "./types";
import { emptyInvitationViewModel } from "./types";

export const useRequestJoinGroupInvitation = (
  invitation: InvitationOfType<"requestJoinGroup"> | undefined,
  options?: UseInvitationOptions,
): InvitationViewModel => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const group = invitation?.group;
  const groupId = group?._id;
  const groupName = group?.name || t("invitations.deletedGroup");
  const inviterName = invitation?.inviterUser?.profile?.fullName;
  const canAccept = !!invitation?.canAccept && !!group;
  const createdByCurrentUser = !!invitation?.createdByCurrentUser;

  const acceptInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.requestJoinGroup.acceptTitle"),
      t("invitations.requestJoinGroup.acceptMessage", {
        inviterName,
        name: group?.name,
      }),
      () => {
        acceptAuthenticatedInvitation(
          invitation,
          () => {
            toastSuccess(t("invitations.requestAccepted"));
            navigate(`/groups/${groupId}`);
          },
          () => toastError(t("invitations.requestAcceptError")),
        );
      },
    );
  }, [group?.name, groupId, invitation, inviterName, navigate, options, t]);

  const rejectInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.requestJoinGroup.rejectTitle"),
      t("invitations.requestJoinGroup.rejectMessage", {
        inviterName,
        name: groupName,
      }),
      () => {
        rejectAuthenticatedInvitation(
          invitation,
          () => toastMessage(t("invitations.requestRejected")),
          () => toastError(t("invitations.requestRejectError")),
        );
      },
    );
  }, [groupName, invitation, inviterName, options, t]);

  const removeInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.requestJoinGroup.removeTitle"),
      t("invitations.requestJoinGroup.removeMessage", { name: groupName }),
      () => {
        removeAuthenticatedInvitation(
          invitation,
          () => toastMessage(t("invitations.removed")),
          () => toastError(t("invitations.removeError")),
        );
      },
    );
  }, [groupName, invitation, options, t]);

  return useMemo(() => {
    if (!invitation) {
      return emptyInvitationViewModel();
    }

    return {
      icon: UsersIcon,
      link: groupId ? `/groups/${groupId}` : null,
      isInvalid: !group,
      text: {
        title: groupName,
        fullTitle: t("invitations.requestJoinGroup.fullPageTitle", {
          name: groupName,
        }),
        youInvited: t("invitations.requestJoinGroup.creatorPostText"),
        hasInvitedYou: t("invitations.requestJoinGroup.receivedPostText"),
        dismiss: createdByCurrentUser
          ? t("invitations.remove")
          : t("invitations.reject"),
        invalid: t("invitations.requestJoinGroup.invalidMessage"),
        ...(groupId ? { goTo: t("invitations.group.view") } : {}),
      },
      acceptInvite: canAccept ? acceptInvite : undefined,
      rejectInvite: !createdByCurrentUser ? rejectInvite : undefined,
      removeInvite: createdByCurrentUser ? removeInvite : undefined,
    };
  }, [
    acceptInvite,
    canAccept,
    createdByCurrentUser,
    group,
    groupId,
    groupName,
    invitation,
    removeInvite,
    rejectInvite,
    t,
  ]);
};
